"use client";

import { useState, useEffect, useMemo } from "react";
import { Search, X, Check, LucideIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// import { Calendar } from "@/components/ui/calendar";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

import { TrainingCard } from "./_components/training-card";
import { TrainingCardSkeleton } from "./_components/training-card-skeleton";

const durationOptions = [
  { value: "1d", label: "1 jour" },
  { value: "2d", label: "2 jours" },
  { value: "3d", label: "3 jours" },
  { value: "1w", label: "1 semaine" },
  { value: "1m", label: "1 mois" },
];

const categoryOptions = [
  { value: "design", label: "Design" },
  { value: "development", label: "Développement" },
  { value: "marketing", label: "Marketing" },
  { value: "business", label: "Business" },
  { value: "data", label: "Data Science" },
  { value: "product", label: "Gestion de Produit" },
  { value: "ux", label: "UX/UI" },
  { value: "agile", label: "Méthodes Agiles" },
  { value: "leadership", label: "Leadership" },
  { value: "finance", label: "Finance" },
];

interface Training {
  id: number;
  icon: string;
  slug: string;
  title: string;
  subtitle: string;
  duration: string;
  hours: string;
  rating: number;
  markAsNew: boolean;
  audience: { title: string; description: string }[];
  features: string[];
  category: string;
  remainingPlaces: number;
}

const fetchTrainings = async (filters: {
  search?: string;
  categories?: string[];
  durations?: string[];
  dateRange?: { from: Date | undefined; to: Date | undefined };
}): Promise<Training[]> => {
  const params = new URLSearchParams();

  if (filters.search) {
    params.append("search", filters.search);
  }
  if (filters.categories?.length) {
    filters.categories.forEach((category) =>
      params.append("categories", category)
    );
  }
  if (filters.durations?.length) {
    filters.durations.forEach((duration) =>
      params.append("durations", duration)
    );
  }
  if (filters.dateRange?.from) {
    params.append("from", filters.dateRange.from.toISOString());
  }
  if (filters.dateRange?.to) {
    params.append("to", filters.dateRange.to.toISOString());
  }

  const response = await fetch(`/api/training?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Failed to fetch trainings");
  }

  const data = await response.json();
  return data.trainings;
};

const ITEMS_PER_PAGE = 6;

export default function Page() {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [customDuration, setCustomDuration] = useState("");
  const [customDurationUnit, setCustomDurationUnit] = useState("days");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [openCategories, setOpenCategories] = useState(false);
  const debouncedSearchTerm = searchTerm;
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFilteredTrainings = async () => {
    setIsLoading(true);
    try {
      const filteredTrainings = await fetchTrainings({
        search: debouncedSearchTerm,
        categories: selectedCategories,
        durations: selectedDurations,
        dateRange: dateRange,
      });
      setTrainings(filteredTrainings);
    } catch (error) {
      console.error("Error fetching trainings:", error);
      setTrainings([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFilteredTrainings();
  }, [searchTerm, selectedCategories, selectedDurations, dateRange]);

  const totalPages = Math.ceil(trainings.length / ITEMS_PER_PAGE);

  const currentTrainings = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return trainings.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, trainings]);

  const handleCategoryChange = (value: string) => {
    setSelectedCategories((prev) => {
      const currentCategories = Array.isArray(prev) ? prev : [];
      return currentCategories.includes(value)
        ? currentCategories.filter((item) => item !== value)
        : [...currentCategories, value];
    });
  };

  const handleDurationChange = (value: string) => {
    setSelectedDurations((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]
    );
  };

  const handleCustomDurationAdd = () => {
    if (customDuration) {
      const newDuration = `${customDuration}${customDurationUnit.charAt(0)}`;
      setSelectedDurations((prev) => [...prev, newDuration]);
      setCustomDuration("");
    }
  };

  const removeFilter = (type: "category" | "duration", value: string) => {
    if (type === "category") {
      setSelectedCategories((prev) =>
        Array.isArray(prev) ? prev.filter((c) => c !== value) : []
      );
    } else if (type === "duration") {
      setSelectedDurations((prev) =>
        Array.isArray(prev) ? prev.filter((d) => d !== value) : []
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 container mx-auto px-4 py-16">
        <div className="space-y-8 my-4">
          <div className="bg-tertiary border border-secondary dark:border-border p-6 rounded-lg space-y-4 max-w-[70rem] mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="w-full rounded-md border px-9 py-2 text-sm"
                  placeholder="Mots-clés, domaines, outils, rôle etc."
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Popover open={openCategories} onOpenChange={setOpenCategories}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openCategories}
                    className="w-[10rem] justify-between border-input hover:border-secondary"
                  >
                    Catégories ({selectedCategories.length})
                    {/* <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" /> */}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Rechercher..." />
                    <CommandList>
                      <CommandEmpty>Aucune catégorie trouvée.</CommandEmpty>
                      <CommandGroup className="max-h-56 overflow-auto">
                        {categoryOptions.map((category) => (
                          <CommandItem
                            key={category.value}
                            onSelect={() =>
                              handleCategoryChange(category.value)
                            }
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                Array.isArray(selectedCategories) &&
                                  selectedCategories.includes(category.value)
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {category.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-input hover:border-secondary"
                  >
                    Durée ({selectedDurations.length})
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[21rem]" align="start">
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      {durationOptions.map((option) => (
                        <div key={option.value} className="flex items-center">
                          <input
                            type="checkbox"
                            id={option.value}
                            checked={selectedDurations.includes(option.value)}
                            onChange={() => handleDurationChange(option.value)}
                            className="mr-2"
                          />
                          <Label htmlFor={option.value}>{option.label}</Label>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <Input
                        type="number"
                        placeholder="Durée"
                        value={customDuration}
                        onChange={(e) => setCustomDuration(e.target.value)}
                        className="w-[5.5rem]"
                      />
                      <Select
                        value={customDurationUnit}
                        onValueChange={setCustomDurationUnit}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Unité" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Heures</SelectItem>
                          <SelectItem value="days">Jours</SelectItem>
                          <SelectItem value="weeks">Semaines</SelectItem>
                          <SelectItem value="months">Mois</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="secondary"
                        onClick={handleCustomDurationAdd}
                      >
                        Ajouter
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              {/* <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="dark:bg-gray-800">
                      Date
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover> */}
              {(selectedCategories.length > 0 ||
                selectedDurations.length > 0 ||
                (dateRange.from && dateRange.to) ||
                searchTerm) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategories([]);
                    setSelectedDurations([]);
                    setDateRange({ from: undefined, to: undefined });
                  }}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                  Réinitialiser
                </Button>
              )}
            </div>

            {(selectedCategories.length > 0 ||
              selectedDurations.length > 0 ||
              (dateRange.from && dateRange.to) ||
              searchTerm) && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                {Array.isArray(selectedCategories) &&
                  selectedCategories.map((category) => (
                    <Badge key={category} variant="secondary" className="gap-1">
                      {categoryOptions.find((c) => c.value === category)
                        ?.label || category}
                      <button
                        className="ml-1 rounded-full hover:bg-muted"
                        onClick={() => removeFilter("category", category)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                {selectedDurations.map((duration) => (
                  <Badge key={duration} variant="secondary" className="gap-1">
                    {durationOptions.find((d) => d.value === duration)?.label ||
                      `${duration.slice(0, -1)} ${customDurationUnit}`}
                    <button
                      className="ml-1 rounded-full hover:bg-muted"
                      onClick={() => removeFilter("duration", duration)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {dateRange.from && dateRange.to && (
                  <Badge variant="secondary" className="gap-1">
                    {dateRange.from.toLocaleDateString()} -{" "}
                    {dateRange.to.toLocaleDateString()}
                    <button
                      className="ml-1 rounded-full hover:bg-muted"
                      onClick={() =>
                        setDateRange({ from: undefined, to: undefined })
                      }
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Trainings */}
          <div className="bg-tertiary dark:bg-black p-6 rounded-lg">
            {isLoading ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, index) => (
                  <TrainingCardSkeleton key={index} />
                ))}
              </div>
            ) : (
              <>
                {currentTrainings.length > 0 ? (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {currentTrainings.map((training) => (
                      <TrainingCard
                        key={training.id}
                        title={training.title}
                        icon={training.icon}
                        slug={training.slug}
                        subtitle={training.subtitle}
                        duration={training.duration}
                        hours={training.hours}
                        rating={training.rating}
                        markAsNew={training.markAsNew}
                        audience={training.audience}
                        features={training.features}
                        className="bg-card"
                        remainingPlaces={training.remainingPlaces}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="rounded-full bg-gray-100 p-3 mb-4">
                      <Search className="h-6 w-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      Aucun résultat trouvé
                    </h3>
                    <p className="text-muted-foreground max-w-sm">
                      Nous n&apos;avons trouvé aucune formation correspondant à
                      vos critères.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-4 dark:bg-gray-800"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategories([]);
                        setSelectedDurations([]);
                        setDateRange({ from: undefined, to: undefined });
                      }}
                    >
                      Réinitialiser les filtres
                    </Button>
                  </div>
                )}
                {currentTrainings.length > 0 && (
                  <div className="flex justify-center mt-6">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage > 1) {
                                setCurrentPage((prev) => prev - 1);
                              }
                            }}
                            className={
                              currentPage === 1
                                ? "pointer-events-none opacity-50"
                                : ""
                            }
                          ></PaginationPrevious>
                        </PaginationItem>
                        {[...Array(totalPages)].map((_, i) => (
                          <PaginationItem key={i}>
                            <PaginationLink
                              href="#"
                              isActive={currentPage === i + 1}
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(i + 1);
                              }}
                            >
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage < totalPages) {
                                setCurrentPage((prev) => prev + 1);
                              }
                            }}
                            className={
                              currentPage === totalPages
                                ? "pointer-events-none opacity-50"
                                : ""
                            }
                          ></PaginationNext>
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
